// @flow
import type { State as Segmentation } from '../../redux/ducks/segmentation/reducers'
import _ from 'lodash'
import type {
  AudienceDataForSegmentation,
  FbPlacements,
  GenderType,
  GroupedObjectParameters,
  GroupedStringParameters,
  LocationsWithChildren
} from '../types'
import separateDiffs from './separateDiffs'
import type { PlacementType } from './PlacementTypeSelect'

const basisDiffs = [{}]
const maxYearsAgeStep = 5

export default function segmentAudience(
  parametersToSegment: AudienceDataForSegmentation,
  segmentation: Segmentation
): {| count: number, dataFunc: () => Array<$Shape<AudienceDataForSegmentation>> |} {
  const ageDiffs: Array<$Shape<AudienceDataForSegmentation>> = segmentation.segmentByAge
    ? getAgeDiffs(parametersToSegment.minAge, parametersToSegment.maxAge)
    : basisDiffs
  const fbPlacementsDiffs: Array<
    $Shape<AudienceDataForSegmentation>
  > = segmentation.segmentByFbPlacements
    ? getFbPlacementsDiffs(parametersToSegment.fbPlacements)
    : basisDiffs
  const genderDiffs: Array<$Shape<AudienceDataForSegmentation>> = segmentation.segmentByGender
    ? getGenderDiffs(parametersToSegment.sex)
    : basisDiffs
  const locationsDiffs: Array<$Shape<AudienceDataForSegmentation>> = segmentation.segmentByLocations
    ? getLocationsDiffs(parametersToSegment)
    : basisDiffs
  const providersDiffs: Array<$Shape<AudienceDataForSegmentation>> = segmentByProviders(
    segmentation
  )
    ? getProvidersDiffs(parametersToSegment.providers)
    : basisDiffs
  const platformDiffs: Array<$Shape<AudienceDataForSegmentation>> = segmentation.segmentByPlatform
    ? getPlatformDiffs(parametersToSegment.platform)
    : basisDiffs
  const vkGroupsDiffs: Array<$Shape<AudienceDataForSegmentation>> = segmentation.segmentByVkGroups
    ? getVkGroupsDiffs(parametersToSegment.includedGroups)
    : basisDiffs
  const vkAppsDiffs: Array<$Shape<AudienceDataForSegmentation>> = segmentation.segmentByVkApps
    ? getVkAppsDiffs(parametersToSegment.includedVkAppsGroups)
    : basisDiffs
  const vkActivityDiffs: Array<
    $Shape<AudienceDataForSegmentation>
  > = segmentation.segmentByVkActivity
    ? getVkActivityDiffs(parametersToSegment.vkActivityGroups)
    : basisDiffs
  const keywordsDiffs: Array<$Shape<AudienceDataForSegmentation>> = segmentation.segmentByKeywords
    ? getKeywordsDiffs(parametersToSegment.keywordsGroups)
    : basisDiffs
  const interestsDiffs: Array<$Shape<AudienceDataForSegmentation>> = segmentation.segmentByInterests
    ? getInterestsDiffs(parametersToSegment)
    : basisDiffs

  const targetingSourcesDiffs: Array<
    $Shape<AudienceDataForSegmentation>
  > = segmentation.segmentByTargetingSources
    ? getTargetingSourcesDiffs(parametersToSegment)
    : basisDiffs
  const placementTypeDiffs: Array<
    $Shape<AudienceDataForSegmentation>
  > = segmentation.segmentByPlacementType
    ? getPlacementTypeDiffs(parametersToSegment.placementTypes)
    : basisDiffs

  const combinedSourcesDiffs: Array<$Shape<AudienceDataForSegmentation>> = combineSourcesDiffs(
    providersDiffs,
    fbPlacementsDiffs,
    vkAppsDiffs,
    vkGroupsDiffs,
    vkActivityDiffs,
    keywordsDiffs,
    interestsDiffs,
    targetingSourcesDiffs,
    parametersToSegment,
    segmentation
  )

  return {
    count:
      ageDiffs.length *
      genderDiffs.length *
      locationsDiffs.length *
      platformDiffs.length *
      placementTypeDiffs.length *
      combinedSourcesDiffs.length,
    dataFunc: () =>
      _.flattenDeep(
        _.map(ageDiffs, (ageDiff) =>
          _.map(genderDiffs, (genderDiff) =>
            _.map(locationsDiffs, (locationsDiff) =>
              _.map(platformDiffs, (platformDiff) =>
                _.map(placementTypeDiffs, (placementTypeDiff) =>
                  _.map(combinedSourcesDiffs, (providersDiff) => ({
                    ...parametersToSegment,
                    ...ageDiff,
                    ...genderDiff,
                    ...locationsDiff,
                    ...platformDiff,
                    ...placementTypeDiff,
                    ...providersDiff
                  }))
                )
              )
            )
          )
        )
      )
  }
}

function getAgeDiffs(
  minAge: number | null,
  maxAge: number | null
): Array<$Shape<AudienceDataForSegmentation>> {
  if (!minAge || !maxAge || maxAge < minAge) return basisDiffs

  const ageGap = maxAge - minAge + 1
  const stepCount = Math.ceil(ageGap / maxYearsAgeStep)
  const minAgeSteps = _.times(
    stepCount,
    (index) => minAge + Math.round((index * ageGap) / stepCount)
  )

  return _.map(minAgeSteps, (minAgeStep, index: number) => ({
    minAge: minAgeStep,
    maxAge: index + 1 === stepCount ? maxAge : minAgeSteps[index + 1] - 1
  }))
}

function getFbPlacementsDiffs(
  fbPlacements: FbPlacements
): Array<$Shape<AudienceDataForSegmentation>> {
  const result: Array<{ fbPlacements: FbPlacements }> = (_.flatten([
    _.map(fbPlacements.audience_network, (position: string) => {
      return { fbPlacements: { audience_network: [position], facebook: ['feed'] } }
    }),
    _.map(
      segmentFbPlacementFacebookPositions(fbPlacements.facebook || []),
      (item: Array<string>) => {
        return { fbPlacements: { facebook: item } }
      }
    ),
    _.map(segmentFbPlacementInstagramPositions(fbPlacements.instagram, fbPlacements.facebook)),
    _.map(fbPlacements.messenger, (position: string) => {
      return { fbPlacements: { messenger: [position] } }
    })
  ]): Array<Object>)
  return result.length === 0 ? basisDiffs : result
}

function segmentFbPlacementFacebookPositions(
  positions: Array<'feed' | 'instant_article' | 'right_hand_column' | 'story'>
): Array<Array<string>> {
  const segments = _.map(_.without(positions, 'feed', 'instant_article', 'story'), (position) => [
    position
  ])

  return [...getInstantArticleFeedSegment(positions), ...segments]
}

function getInstantArticleFeedSegment(
  positions: Array<'feed' | 'instant_article' | 'right_hand_column' | 'story'>
): Array<Array<string>> {
  const feedItems: Array<string> = []
  if (_.includes(positions, 'feed')) feedItems.push('feed')
  if (_.includes(positions, 'instant_article')) feedItems.push('instant_article')

  return feedItems.length === 0 ? [] : [feedItems]
}

function segmentFbPlacementInstagramPositions(
  instagramPositions?: Array<'stream' | 'story'>,
  facebookPositions?: Array<'feed' | 'instant_article' | 'right_hand_column' | 'story'>
) {
  return _.map(instagramPositions, (position: string) => {
    if (position === 'story' && _.includes(facebookPositions, 'story')) {
      return { fbPlacements: { instagram: ['story'], facebook: ['story'] } }
    } else {
      return { fbPlacements: { instagram: [position] } }
    }
  })
}

function getGenderDiffs(gender: GenderType): Array<$Shape<AudienceDataForSegmentation>> {
  if (gender !== 'both') return basisDiffs

  return [{ sex: 'male' }, { sex: 'female' }]
}

function getLocationsDiffs(
  parametersToSegment: AudienceDataForSegmentation
): Array<$Shape<AudienceDataForSegmentation>> {
  const {
    localGeo,
    includedLocationGroups,
    excludedLocationIds,
    locationsWithChildren
  } = parametersToSegment
  if (localGeo.length === 0 && includedLocationGroups.length === 0) return basisDiffs

  if (includedLocationGroups.length === 0) {
    return _.map(localGeo, (geo) => ({
      localGeo: [geo]
    }))
  } else if (includedLocationGroups.length === 1) {
    return _.map(_.first(includedLocationGroups).value, (id) => {
      return {
        includedLocationGroups: [{ value: [id], name: '' }],
        excludedLocationIds: locationsToExclude([id], excludedLocationIds, locationsWithChildren)
      }
    })
  } else {
    return _.map(includedLocationGroups, (group) => {
      return {
        includedLocationGroups: [group],
        excludedLocationIds: locationsToExclude(
          group.value,
          excludedLocationIds,
          locationsWithChildren
        )
      }
    })
  }
}

function locationsToExclude(
  includedIds: Array<number>,
  excludedIds: Array<number>,
  locationsWithChildren: LocationsWithChildren
): Array<number> {
  return _.flatMap(includedIds, (includedId) => {
    const children = locationsWithChildren[includedId]
    return children ? _.intersection(excludedIds, children) : []
  })
}

function getProvidersDiffs(providers: Array<string>): Array<$Shape<AudienceDataForSegmentation>> {
  if (providers.length === 0) return basisDiffs

  return _.map(providers, (provider) => ({
    providers: [provider]
  }))
}

function combineFbPlacementDiffsWithProvidersDiff(
  fbPlacementsDiffs: Array<$Shape<AudienceDataForSegmentation>>,
  providersDiffs
): Array<$Shape<AudienceDataForSegmentation>> {
  return _.flattenDeep(
    _.map(providersDiffs, (providersDiff) => {
      if (_.isEqual(providersDiff.providers, ['facebook'])) {
        return _.map(fbPlacementsDiffs, (fbPlacementsDiff) => {
          return { ...providersDiff, ...fbPlacementsDiff }
        })
      } else {
        return { ...providersDiff, fbPlacements: {} }
      }
    })
  )
}

function getPlatformDiffs(
  platform: 'all' | 'desktop' | 'mobile'
): Array<$Shape<AudienceDataForSegmentation>> {
  if (platform !== 'all') return basisDiffs
  return [{ platform: 'desktop' }, { platform: 'mobile' }]
}

function getVkGroupsDiffs(
  includedGroups: Array<GroupedObjectParameters>
): Array<$Shape<AudienceDataForSegmentation>> {
  if (includedGroups.length === 0) return basisDiffs

  if (includedGroups.length === 1) {
    return _.map(_.first(includedGroups).value, (id) => ({
      includedGroups: [{ value: [id], name: '' }]
    }))
  } else {
    return _.map(includedGroups, (group) => ({
      includedGroups: [group]
    }))
  }
}

function getVkAppsDiffs(
  includedVkAppsGroups: Array<GroupedStringParameters>
): Array<$Shape<AudienceDataForSegmentation>> {
  if (includedVkAppsGroups.length === 0) return basisDiffs

  if (includedVkAppsGroups.length === 1) {
    const includedVkAppsGroup = _.first(includedVkAppsGroups).value
    if (_.isEmpty(includedVkAppsGroup) || includedVkAppsGroup.split('\n').length === 0)
      return basisDiffs

    return _.map(includedVkAppsGroup.split('\n'), (vkApp) => ({
      includedVkAppsGroups: [{ value: vkApp, name: '' }]
    }))
  } else {
    return _.map(includedVkAppsGroups, (group) => ({
      includedVkAppsGroups: [group]
    }))
  }
}

function getVkActivityDiffs(
  vkActivityGroups: Array<GroupedObjectParameters>
): Array<$Shape<AudienceDataForSegmentation>> {
  if (vkActivityGroups.length === 0) return basisDiffs

  if (vkActivityGroups.length === 1) {
    return _.map(_.first(vkActivityGroups).value, (id) => ({
      vkActivityGroups: [{ value: [id], name: '' }]
    }))
  } else {
    return _.map(vkActivityGroups, (group) => ({
      vkActivityGroups: [group]
    }))
  }
}

function getKeywordsDiffs(
  keywordsGroups: Array<GroupedStringParameters>
): Array<$Shape<AudienceDataForSegmentation>> {
  if (keywordsGroups.length === 0) return basisDiffs

  if (keywordsGroups.length === 1) {
    const keywordsGroup = _.first(keywordsGroups).value
    if (_.isEmpty(keywordsGroup) || keywordsGroup.split('\n').length === 0) return basisDiffs

    return _.map(keywordsGroup.split('\n'), (keyword) => ({
      keywordsGroups: [{ value: keyword, name: '' }]
    }))
  } else {
    return _.map(keywordsGroups, (group) => ({
      keywordsGroups: [group]
    }))
  }
}

function getInterestsDiffs(
  parametersToSegment: AudienceDataForSegmentation
): Array<$Shape<AudienceDataForSegmentation>> {
  const { interestIds, fbAdInterests } = parametersToSegment
  if (interestIds.length === 0 && fbAdInterests.length === 0) return basisDiffs

  const fbAdInterestsDiffs = _.map(fbAdInterests, (fbAdInterest) => ({
    fbAdInterests: [fbAdInterest],
    interestIds: []
  }))
  const interestIdsDiffs = _.map(interestIds, (interestId) => ({
    interestIds: [interestId],
    fbAdInterests: []
  }))
  return [...fbAdInterestsDiffs, ...interestIdsDiffs]
}

function combineInterestsDiffsWithProvidersDiff(interestsDiffs, providersDiffs, providerInterests) {
  const interestIdsDiffs = _.filter(interestsDiffs, (diff) => !_.isEmpty(diff.interestIds))
  const fbAdInterestsDiffs = _.filter(interestsDiffs, (diff) => !_.isEmpty(diff.fbAdInterests))
  return _.flattenDeep(
    _.map(providersDiffs, (providersDiff) => {
      if (_.isEmpty(providersDiff) || _.isEmpty(providersDiff.providers)) return interestsDiffs
      const combinedInterests = combineInterestIdsDiffs(
        interestIdsDiffs,
        providersDiff,
        providerInterests
      )
      const combinedFbAdInterests = combineFbAdInterestsDiffs(fbAdInterestsDiffs, providersDiff)
      const result = _.uniqWith([...combinedInterests, ...combinedFbAdInterests], _.isEqual)
      return _.isEmpty(result) ? providersDiff : result
    })
  )
}

function combineInterestIdsDiffs(interestIdsDiffs, providersDiff, providerInterests) {
  return _.compact(
    _.map(interestIdsDiffs, (interestIdsDiff) => {
      if (_.isEmpty(interestIdsDiff)) return null
      if (_.isEmpty(interestIdsDiff.interestIds)) return null
      const provider = _.first(providersDiff.providers)
      const interestId = _.first(interestIdsDiff.interestIds)
      const providerInterest = _.find(providerInterests, (providerInterest) => {
        return providerInterest.id === interestId
      })
      if (!providerInterest || providerInterest.provider !== provider) return null
      return {
        ...providersDiff,
        interestIds: interestIdsDiff.interestIds,
        fbAdInterests: []
      }
    })
  )
}

function combineFbAdInterestsDiffs(fbAdInterestsDiffs, providersDiff) {
  return _.compact(
    _.map(fbAdInterestsDiffs, (fbAdInterestDiff) => {
      if (_.isEmpty(fbAdInterestDiff.fbAdInterests)) return null
      if (!_.isEqual(providersDiff.providers, ['facebook'])) return null
      return {
        ...providersDiff,
        interestIds: [],
        fbAdInterests: fbAdInterestDiff.fbAdInterests
      }
    })
  )
}

function combineVkAppsDiffsWithProvidersDiff(vkAppsDiffs, providersDiffs) {
  return _.flattenDeep(
    _.map(providersDiffs, (providersDiff) => {
      if (
        _.isEqual(providersDiff.providers, ['vk']) ||
        _.isEqual(providersDiff.providers, ['target_mail'])
      ) {
        return _.map(vkAppsDiffs, (vkAppsDiff) => {
          return { ...providersDiff, ...vkAppsDiff }
        })
      } else {
        return { ...providersDiff, includedVkAppsGroups: [{ value: '', name: '' }] }
      }
    })
  )
}

function combineVkGroupsDiffsWithProvidersDiff(vkGroupsDiffs, providersDiffs) {
  return _.flattenDeep(
    _.map(providersDiffs, (providersDiff) => {
      if (
        _.isEqual(providersDiff.providers, ['vk']) ||
        _.isEqual(providersDiff.providers, ['target_mail'])
      ) {
        return _.map(vkGroupsDiffs, (vkGroupsDiff) => {
          return { ...providersDiff, ...vkGroupsDiff }
        })
      } else {
        return { ...providersDiff, includedGroups: [{ value: [], name: '' }] }
      }
    })
  )
}

function combineVkActivityDiffsWithProvidersDiff(vkActivityDiffs, providersDiffs) {
  return _.flattenDeep(
    _.map(providersDiffs, (providersDiff) => {
      if (_.isEqual(providersDiff.providers, ['vk'])) {
        return _.map(vkActivityDiffs, (vkActivityDiff) => {
          return { ...providersDiff, ...vkActivityDiff }
        })
      } else {
        return { ...providersDiff, vkActivityGroups: [{ value: [], name: '' }] }
      }
    })
  )
}

function combineKeywordsDiffsWithProvidersDiff(keywordsDiffs, providersDiffs) {
  return _.flattenDeep(
    _.map(providersDiffs, (providersDiff) => {
      if (
        _.isEqual(providersDiff.providers, ['google']) ||
        _.isEqual(providersDiff.providers, ['target_mail'])
      ) {
        return _.map(keywordsDiffs, (keywordsDiff) => {
          return { ...providersDiff, ...keywordsDiff }
        })
      } else {
        return { ...providersDiff, keywordsGroups: [{ value: '', name: '' }] }
      }
    })
  )
}

function getTargetingSourcesDiffs(
  parametersToSegment: AudienceDataForSegmentation
): Array<$Shape<AudienceDataForSegmentation>> {
  const targetingSources = buildTargetingSources(parametersToSegment)
  if (
    _.every(_.values(targetingSources), (sourceValues) => {
      return _.isEmpty(sourceValues)
    })
  ) {
    return basisDiffs
  }

  return [
    ...targetingSourceDiffs(
      targetingSources.includedSegmentIds,
      'includedSegmentIds',
      parametersToSegment.providers
    ),
    ...targetingSourceDiffs(
      targetingSources.includedContactIds,
      'includedContactIds',
      parametersToSegment.providers
    ),
    ...targetingSourceDiffs(
      targetingSources.includedLookalikeSegmentIds,
      'includedLookalikeSegmentIds',
      parametersToSegment.providers
    ),
    ...targetingSourceDiffs(
      targetingSources.includedLookalikeContactIds,
      'includedLookalikeContactIds',
      parametersToSegment.providers
    ),
    ...targetingSourceDiffs(
      targetingSources.includedProviderTargetingSourceIds,
      'includedProviderTargetingSourceIds',
      parametersToSegment.providers
    )
  ]
}

function targetingSourceDiffs(
  source: Array<number>,
  sourceName: string,
  providers: Array<string>
): Array<$Shape<AudienceDataForSegmentation>> {
  if (_.isEqual(providers, ['google']) && sourceName !== 'includedSegmentIds')
    return [{ [sourceName]: [] }]
  return _.map(source, (sourceId) => ({
    [sourceName]: [sourceId]
  }))
}

function getPlacementTypeDiffs(
  placementTypes: Array<PlacementType>
): Array<$Shape<AudienceDataForSegmentation>> {
  return _.map(placementTypes, (item) => ({ placementTypes: [item] }))
}

function combineTargetingSourcesDiffsWithProvidersDiff(
  targetingSourcesDiffs,
  providersDiffs,
  targetingSources
) {
  return _.flattenDeep(
    _.map(providersDiffs, (providersDiff) => {
      const includedSegments = combineIncludedSegmentDiffs(targetingSourcesDiffs, providersDiff)
      const includedContacts = combineIncludedContactDiffs(
        targetingSourcesDiffs,
        providersDiff,
        targetingSources
      )
      const includedLookalikeSegments = combineIncludedLookalikeSegmentDiffs(
        targetingSourcesDiffs,
        providersDiff
      )
      const includedLookalikeContacts = combineIncludedLookalikeContactDiffs(
        targetingSourcesDiffs,
        providersDiff
      )
      const includedProviderTargetingSources = combineIncludedProviderTargetingSourceDiffs(
        targetingSourcesDiffs,
        providersDiff,
        targetingSources
      )
      const result = _.uniqWith(
        [
          ...includedSegments,
          ...includedContacts,
          ...includedLookalikeSegments,
          ...includedLookalikeContacts,
          ...includedProviderTargetingSources
        ],
        _.isEqual
      )
      return _.isEmpty(result) ? providersDiff : result
    })
  )
}

function combineIncludedSegmentDiffs(targetingSourcesDiffs, providersDiff) {
  return _.compact(
    _.map(targetingSourcesDiffs, (targetingSourcesDiff) => {
      if (_.isEmpty(targetingSourcesDiff.includedSegmentIds)) return null
      return {
        ...providersDiff,
        includedSegmentIds: targetingSourcesDiff.includedSegmentIds,
        includedContactIds: [],
        includedLookalikeSegmentIds: [],
        includedLookalikeContactIds: [],
        includedProviderTargetingSourceIds: []
      }
    })
  )
}

function combineIncludedContactDiffs(targetingSourcesDiffs, providersDiff, targetingSources) {
  return _.compact(
    _.map(targetingSourcesDiffs, (targetingSourcesDiff) => {
      if (_.isEqual(providersDiff.providers, ['google'])) return null
      if (_.isEmpty(targetingSourcesDiff.includedContactIds)) return null
      const contact = _.find(targetingSources.contacts, (contact) => {
        return contact.id === _.first(targetingSourcesDiff.includedContactIds)
      })
      if (_.isEqual(providersDiff.providers, ['facebook']) && contact && contact.kind === 'vk_id')
        return null
      return {
        ...providersDiff,
        includedSegmentIds: [],
        includedContactIds: targetingSourcesDiff.includedContactIds,
        includedLookalikeSegmentIds: [],
        includedLookalikeContactIds: [],
        includedProviderTargetingSourceIds: []
      }
    })
  )
}

function combineIncludedLookalikeSegmentDiffs(targetingSourcesDiffs, providersDiff) {
  return _.compact(
    _.map(targetingSourcesDiffs, (targetingSourcesDiff) => {
      if (_.isEqual(providersDiff.providers, ['google'])) return null
      if (_.isEmpty(targetingSourcesDiff.includedLookalikeSegmentIds)) return null
      return {
        ...providersDiff,
        includedSegmentIds: [],
        includedContactIds: [],
        includedLookalikeSegmentIds: targetingSourcesDiff.includedLookalikeSegmentIds,
        includedLookalikeContactIds: [],
        includedProviderTargetingSourceIds: []
      }
    })
  )
}

function combineIncludedLookalikeContactDiffs(targetingSourcesDiffs, providersDiff) {
  return _.compact(
    _.map(targetingSourcesDiffs, (targetingSourcesDiff) => {
      if (_.isEqual(providersDiff.providers, ['google'])) return null
      if (_.isEmpty(targetingSourcesDiff.includedLookalikeContactIds)) return null
      return {
        ...providersDiff,
        includedSegmentIds: [],
        includedContactIds: [],
        includedLookalikeSegmentIds: [],
        includedLookalikeContactIds: targetingSourcesDiff.includedLookalikeContactIds,
        includedProviderTargetingSourceIds: []
      }
    })
  )
}

function combineIncludedProviderTargetingSourceDiffs(
  targetingSourcesDiffs,
  providersDiff,
  targetingSources
) {
  return _.compact(
    _.map(targetingSourcesDiffs, (targetingSourcesDiff) => {
      if (_.isEqual(providersDiff.providers, ['google'])) return null
      if (_.isEmpty(targetingSourcesDiff.includedProviderTargetingSourceIds)) return null
      const sources = _.filter(targetingSources.providerTargetingSources, (source) => {
        return (
          _.includes(providersDiff.providers, source.provider) &&
          _.includes(targetingSourcesDiff.includedProviderTargetingSourceIds, source.id)
        )
      })
      if (_.isEmpty(sources)) return null
      return {
        ...providersDiff,
        includedSegmentIds: [],
        includedContactIds: [],
        includedLookalikeSegmentIds: [],
        includedLookalikeContactIds: [],
        includedProviderTargetingSourceIds: _.map(sources, (s) => s.id)
      }
    })
  )
}

function segmentByProviders(segmentation) {
  const {
    segmentByProviders,
    segmentByFbPlacements,
    segmentByVkApps,
    segmentByVkActivity,
    segmentByVkGroups,
    segmentByKeywords,
    segmentByTargetingSources
  } = segmentation
  return _.some([
    segmentByProviders,
    segmentByFbPlacements,
    segmentByVkApps,
    segmentByVkActivity,
    segmentByVkGroups,
    segmentByKeywords,
    segmentByTargetingSources
  ])
}

function combineSourcesDiffs(
  providersDiffs,
  fbPlacementsDiffs,
  vkAppsDiffs,
  vkGroupsDiffs,
  vkActivityDiffs,
  keywordsDiffs,
  interestDiffs,
  targetingSourcesDiffs,
  parametersToSegment: AudienceDataForSegmentation,
  segmentation
): Array<$Shape<AudienceDataForSegmentation>> {
  let result = providersDiffs
  result = combineFbPlacementDiffsWithProvidersDiff(fbPlacementsDiffs, result)
  result = combineVkAppsDiffsWithProvidersDiff(vkAppsDiffs, result)
  result = combineVkGroupsDiffsWithProvidersDiff(vkGroupsDiffs, result)
  result = combineVkActivityDiffsWithProvidersDiff(vkActivityDiffs, result)
  result = combineKeywordsDiffsWithProvidersDiff(keywordsDiffs, result)
  result = combineInterestsDiffsWithProvidersDiff(
    interestDiffs,
    result,
    parametersToSegment.providerInterests
  )
  result = combineTargetingSourcesDiffsWithProvidersDiff(
    targetingSourcesDiffs,
    result,
    buildTargetingSources(parametersToSegment)
  )
  return separateDiffs(result, parametersToSegment, segmentation)
}

function buildTargetingSources(parametersToSegment) {
  return {
    includedSegmentIds: parametersToSegment.includedSegmentIds,
    includedContactIds: parametersToSegment.includedContactIds,
    includedLookalikeSegmentIds: parametersToSegment.includedLookalikeSegmentIds,
    includedLookalikeContactIds: parametersToSegment.includedLookalikeContactIds,
    includedProviderTargetingSourceIds: parametersToSegment.includedProviderTargetingSourceIds,
    segments: parametersToSegment.segments,
    contacts: parametersToSegment.contacts,
    lookalikeSegments: parametersToSegment.lookalikeSegments,
    lookalikeContacts: parametersToSegment.lookalikeContacts,
    providerTargetingSources: parametersToSegment.providerTargetingSources
  }
}
